import { XMarkIcon } from '@heroicons/react/24/outline';
import {
    ActionIcon,
    Paper,
    ScrollArea,
    Stack,
    useMantineColorScheme
} from '@mantine/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { WorkExperienceModel } from '../../../features/CVEditor/features/WorkExperience';
import { Person } from '../../../models/person.model';
import { TechnologyExperience } from '../../../models/technologyExperience.model';
import { ExportService } from '../../../services/exportService';
import { PersonService } from '../../../services/personService';
import { PersonTechnologyService } from '../../../services/personTechnologyService';
import { ProjectService } from '../../../services/projectService';
import { TechnologyService } from '../../../services/technologyService';
import { CVBottomNavigation } from './CVBottomNavigation';
import { CVEmployeeSection } from './CVEmployeeSection';
import { CVExperienceHighlights } from './CVExperienceHighlights';
import { CVPreviewHeader } from './CVPreviewHeader';
import { CVRolesSection } from './CVRolesSection';
import { CVSkillsSection } from './CVSkillsSection';

interface CVPreviewSidebarProps {
    personId: number | null;
    onClose: () => void;
    onDownloadCV?: (format?: string) => void;
    isEditable?: boolean;
    onRefresh?: () => void;
}

export function CVPreviewSidebar({
    personId,
    onClose,
    onDownloadCV,
    isEditable = true,
    onRefresh
}: CVPreviewSidebarProps) {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const [person, setPerson] = useState<Person | null>(null);
    const loadingRef = useRef<boolean>(false);
    const [workExperiences] = useState<WorkExperienceModel[]>([]);
    const [skills, setSkills] = useState<string[]>([]);
    const [technologyExperience, setTechnologyExperience] = useState<TechnologyExperience[]>([]);
    const [roles, setRoles] = useState<string[]>([]);
    const [refreshCounter, setRefreshCounter] = useState(0);

    // Cache services with useRef to prevent recreation on each render
    const servicesRef = React.useRef({
        personService: new PersonService(),
        technologyService: new TechnologyService(),
        projectService: new ProjectService(),
        exportService: new ExportService(),
        personTechnologyService: new PersonTechnologyService()
    });

    const loadPersonData = useCallback(async () => {
        if (!personId) return;

        // Prevent multiple simultaneous loads using ref
        if (loadingRef.current) return;

        loadingRef.current = true;

        try {
            const personData = await servicesRef.current.personService.getPersonById(personId);
            setPerson(personData);

            // Load favorite technologies and their experience
            try {
                const [favoriteTechnologies, techExperience] = await Promise.all([
                    servicesRef.current.personTechnologyService.getPersonFavoriteTechnologies(personId),
                    servicesRef.current.projectService.getTechnologyExperienceByPerson(personId)
                ]);

                // Extract technology names
                const techNames = favoriteTechnologies.map((tech: any) => tech.technology.name);
                setSkills(techNames);
                setTechnologyExperience(techExperience);
            } catch (error) {
                console.error('Error loading favorite technologies:', error);
                // Fallback to person.technologies if available
                setSkills(personData.technologies || []);
                setTechnologyExperience([]);
            }

            // Load projects to extract roles
            try {
                const projectsData = await servicesRef.current.projectService.getProjectByPersonID(personId);

                // Extract unique roles from projects
                const uniqueRoles = new Set<string>();
                projectsData.forEach(project => {
                    if (project.roles && Array.isArray(project.roles)) {
                        project.roles.forEach(role => {
                            const roleName = (role as any).name;
                            if (roleName) {
                                uniqueRoles.add(roleName);
                            }
                        });
                    }
                });
                setRoles(Array.from(uniqueRoles));
            } catch (error) {
                console.error('Error loading projects data:', error);
            }

        } catch (error) {
            console.error('Error loading person data:', error);
        } finally {
            loadingRef.current = false;
        }
    }, [personId]);

    const handleRefreshData = useCallback(() => {
        setRefreshCounter(prev => prev + 1);
    }, []);

    useEffect(() => {
        loadPersonData();
    }, [personId, refreshCounter, loadPersonData]);

    if (!person || !personId) return null;

    // Get top experience entries
    const topExperience = workExperiences.slice(0, 3).map(exp => ({
        id: exp.id.toString(),
        position: exp.position,
        company: exp.employer,
        duration: `${new Date(exp.dateStart).getFullYear()} - ${exp.dateEnd ? new Date(exp.dateEnd).getFullYear() : 'Present'}`,
        description: exp.description || ''
    }));

    return (
        <Paper
            radius={0}
            h="100%"
            w="100%"
            style={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-0)',
                position: 'relative',
                borderRadius: 0,
                borderLeft: '1px solid var(--mantine-color-gray-3)',
                boxShadow: '-5px 0 15px rgba(0, 0, 0, 0.05)'
            }}
        >
            {/* Close button - only show when editable */}
            {isEditable && (
                <ActionIcon
                    onClick={onClose}
                    style={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}
                    variant="filled"
                    color="gray"
                    radius="xl"
                    size="lg"
                >
                    <XMarkIcon style={{ width: 20, height: 20 }} />
                </ActionIcon>
            )}

            {/* Header section */}
            <CVPreviewHeader
                person={person}
            />

            {/* Scrollable content area */}
            <ScrollArea style={{ flex: 1 }} offsetScrollbars scrollbarSize={8}>
                <Stack gap="md" p="md">
                    <CVEmployeeSection person={person} />
                    <CVSkillsSection
                        skills={skills}
                        technologyExperience={technologyExperience}
                    />

                    <CVRolesSection roles={roles} />

                    <CVExperienceHighlights
                        experiences={topExperience}
                        totalCount={workExperiences.length}
                    />
                </Stack>
            </ScrollArea>

            {/* Bottom navigation - only show when editable */}
            {isEditable && (
                <CVBottomNavigation
                    personId={person.id}
                    person={person}
                    fullName={`${person.firstName} ${person.lastName}`}
                    onClose={onClose}
                    onRefresh={onRefresh}
                    onRefreshData={handleRefreshData}
                />
            )}
        </Paper>
    );
}

